import React, { useMemo } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import {
  Table as TableBase,
  TableRow as TableRowBase,
  TableCell as TableCellBase,
  TableBody as TableBodyBase,
  TableHead as TableHeadBase
} from '@mui/material'
import {useTranslation} from 'react-i18next';
import { useOptionalSidebar } from 'components/AuthenticatedLayout/Sidebar/SidebarContext'

import {
  ITable,
  ICtx,
  IColumn,
  IOutputTable,
  IComponents,
  IItem
} from './models'

import TableRowLoading from './TableRowLoading'
import TableHeaderSortableCell from './TableHeaderSortableCell'

const defaultComponents:IComponents = {
  Table: TableBase,
  TableRowLoading: TableRowLoading,
  TableHead: TableHeadBase,
  TableHeadRow: TableRowBase,
  TableBody: TableBodyBase,
  TableBodyRow: TableRowBase,
  TableHeaderCell: TableCellBase,
  TableHeaderSortableCell: TableHeaderSortableCell,
  TableBodyCell: TableCellBase,
}

export { defaultComponents as components}

const useTable = ({
  items,
  rowKey = 'uuid',
  columns,
  loading,
  loadingNext = false,
  components = defaultComponents,
  onSendAction = () => {},
  onRowClick,
  rowIsOnError = () => false,
  rowIsBold = () => false,
  expandedRowRender,
  responsiveConfig
}: ITable): IOutputTable => {
  const {t} = useTranslation()
  const theme = useTheme()
  const sidebar = useOptionalSidebar()
  const setSidebarExpanded = sidebar?.setExpanded

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'))
  
  const screenSize = useMemo(() => {
    if (isMobile) return 'mobile'
    if (isTablet) return 'tablet'
    return 'desktop'
  }, [isMobile, isTablet])

  const ctx = useMemo(():ICtx => {
    return {
      t,
      items,
      onRowClick: onRowClick
        ? (e, item) => {
            setSidebarExpanded?.(false)
            onRowClick(item)
          }
        : undefined,
      onSendAction: (e, name, item) => {
        e.stopPropagation()
        onSendAction(name, item)
      }
    }
  }, [items, onRowClick, onSendAction, setSidebarExpanded])

  const enhancedExpandedRowRender = useMemo(() => {
    if (!responsiveConfig?.expandedRowRenderer) {
      return expandedRowRender
    }

    const { component: ExpandedComponent, shouldRender, getVisibleColumns } = responsiveConfig.expandedRowRenderer

    if (!shouldRender(screenSize)) {
      return undefined
    }

    return (item: IItem, ctx: ICtx) => {
      const visibleColumnsForScreen = getVisibleColumns(screenSize)
      return React.createElement(ExpandedComponent, {
        item: item,
        ctx: ctx,
        visibleColumns: visibleColumnsForScreen
      })
    }
  }, [expandedRowRender, responsiveConfig, screenSize])

  return {
    loading,
    loadingNext,
    columns: columns,
    rowKey,
    components: {
      Table: components.Table || TableBase,
      TableRowLoading: components.TableRowLoading || TableRowLoading,
      TableHead: components.TableHead || TableHeadBase,
      TableHeadRow: components.TableHeadRow || TableRowBase,
      TableBody: components.TableBody || TableBodyBase,
      TableBodyRow: components.TableBodyRow || TableRowBase,
      TableHeaderCell: components.TableHeaderCell || TableCellBase,
      TableHeaderSortableCell: components.TableHeaderSortableCell || TableHeaderSortableCell,
      TableBodyCell: components.TableBodyCell || TableCellBase,
    },
    headerColumnContent: (name) => {
      const column:IColumn = <IColumn>columns.find(column => name === column.name)
      return column.header.label(ctx)
    },
    bodyColumnContent: (name, item) => {
      const column:IColumn = <IColumn>columns.find(column => name === column.name)
      return column
        ? column.value(item, ctx) || ''
        : ''
    },
    onRowClick: onRowClick
      ? (e, item) => {
          e.stopPropagation()
          setSidebarExpanded?.(false)
          onRowClick(item)
        }
      : undefined,
    rowIsOnError: (item) => {
      return rowIsOnError(item)
    },
    rowIsBold: (item) => {
      return rowIsBold(item)
    },
    sendAction: (e, name, item) => {
      e.stopPropagation()
      onSendAction(name, item)
    },
    items,
    expandedRowRender: enhancedExpandedRowRender
  }
}

export default useTable
